import { DescriptionOutlined, ExpandLess, ExpandMore, OpenInNew } from '@mui/icons-material'
import { Box, Button, Collapse, Grid, IconButton, Typography, useMediaQuery } from '@mui/material'
import React, { useState } from 'react'
import { getAdoptionAttemptDocumentsSrc } from '../../../../utils/S3-links'
import { DisplayChipLikeTextField } from '../../../common/DisplayChipLikeTextField'
import { blueSky, theme } from '../../../theme'
import { AdoptionAttemptDocumentDtoType } from '../../../../interactors/gen/backendClient'
import { getReadableAdoptionDocumentType } from '../../../../domain/Adopters/AccountDisplay'

interface DocumentItemProps {
  type: 'certificate' | 'contract' | 'sterilization' | AdoptionAttemptDocumentDtoType
  documentKey: string
  uploadedAt: string
  fullName: string
}

export const AdoptionDocumentItem: React.FC<DocumentItemProps> = ({ type, documentKey, uploadedAt, fullName }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const showUploaderInline = useMediaQuery('(min-width:1050px)')
  const showInlineMeta = !isMobile
  const [isExpanded, setIsExpanded] = useState(false)

  const translatedType =
    type === 'certificate'
      ? 'Certificat de connaissance signé'
      : type === 'sterilization'
      ? 'Certificat de stérilisation'
      : type === 'contract'
      ? "Contrat d'adoption signé"
      : getReadableAdoptionDocumentType(type)

  return (
    <Box
      mt={2}
      sx={{
        py: { xs: 1.5, sm: 1.75 },
        borderBottom: '1px solid rgba(122, 129, 151, 0.16)',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Grid
        container
        wrap="nowrap"
        alignItems="center"
        columnSpacing={{ xs: 1, sm: 1.25, md: 1.5 }}
        sx={{ margin: 0, width: '100%' }}
      >
        <Grid item sx={{ width: { xs: 36, sm: 42 }, flex: '0 0 auto' }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              width: { xs: 32, sm: 36 },
              height: { xs: 32, sm: 36 },
              borderRadius: 2.5,
              backgroundColor: 'rgba(105, 141, 172, 0.10)',
              border: '1px solid rgba(105, 141, 172, 0.16)',
            }}
          >
            <DescriptionOutlined sx={{ width: { xs: 18, sm: 20 }, height: { xs: 18, sm: 20 }, color: blueSky }} />
          </Box>
        </Grid>

        <Grid item xs zeroMinWidth>
          {showInlineMeta ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: showUploaderInline
                  ? 'minmax(210px, 0.85fr) minmax(120px, 0.55fr) minmax(170px, 0.72fr) minmax(145px, 0.7fr)'
                  : 'minmax(210px, 0.95fr) minmax(125px, 0.58fr) minmax(190px, 0.78fr)',
                columnGap: { sm: 1.75, md: 2.5 },
                alignItems: 'center',
                minWidth: 0,
              }}
            >
              <Box sx={{ minWidth: 0, minHeight: 42, display: 'flex', alignItems: 'center' }}>
                <DisplayChipLikeTextField
                  label=""
                  value={translatedType}
                  fullWidth
                  documentPage
                  color={blueSky}
                  compact
                  borderless
                  emphasis
                  maxChipWidth={250}
                />
              </Box>

              <Box
                minWidth={0}
                sx={{
                  minHeight: 42,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#7A8197',
                    fontSize: { xs: 10.5, sm: 11 },
                    fontWeight: 500,
                    lineHeight: 1.2,
                    mb: 0.25,
                  }}
                >
                  Signé le
                </Typography>
                <Typography
                  sx={{
                    color: '#203864',
                    fontSize: { xs: 12.25, sm: 12.75, md: 13 },
                    fontWeight: 400,
                    lineHeight: 1.25,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {new Date(uploadedAt).toLocaleDateString('fr-FR')}
                </Typography>
              </Box>

              {showUploaderInline && <Box minWidth={0} sx={{ minHeight: 42 }} />}

              {showUploaderInline && (
                <Box
                  minWidth={0}
                  sx={{ minHeight: 42, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#7A8197',
                      fontSize: { xs: 10.5, sm: 11 },
                      fontWeight: 500,
                      lineHeight: 1.2,
                      mb: 0.25,
                    }}
                  >
                    Par
                  </Typography>
                  <Typography
                    sx={{
                      color: '#203864',
                      fontSize: { xs: 12.25, sm: 12.75, md: 13 },
                      fontWeight: 400,
                      lineHeight: 1.25,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={fullName}
                  >
                    {fullName}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ minWidth: 0 }}>
              <DisplayChipLikeTextField
                label=""
                value={translatedType}
                fullWidth
                documentPage
                color={blueSky}
                compact
                borderless
                emphasis
                maxChipWidth={220}
              />
            </Box>
          )}
        </Grid>

        <Grid item sx={{ flex: '0 0 auto' }}>
          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={{ xs: 0.15, sm: 0.35 }}>
            {isMobile && (
              <IconButton
                size="small"
                onClick={() => setIsExpanded((prev) => !prev)}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 2,
                  color: '#D39A12',
                  backgroundColor: 'rgba(243, 172, 0, 0.08)',
                  '&:hover': { backgroundColor: 'rgba(243, 172, 0, 0.16)' },
                }}
              >
                {isExpanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
              </IconButton>
            )}
            {isMobile ? (
              <IconButton
                size="small"
                href={getAdoptionAttemptDocumentsSrc(documentKey)}
                target="_blank"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 2,
                  color: theme.palette.primary.main,
                  backgroundColor: 'rgba(32, 56, 100, 0.08)',
                  '&:hover': { backgroundColor: 'rgba(32, 56, 100, 0.14)' },
                }}
              >
                <OpenInNew sx={{ fontSize: 18 }} />
              </IconButton>
            ) : (
              <Button
                variant="outlined"
                href={getAdoptionAttemptDocumentsSrc(documentKey)}
                target="_blank"
                sx={{
                  minHeight: 34,
                  px: 1.6,
                  borderRadius: '999px',
                  textTransform: 'none',
                  fontSize: 12.5,
                  fontWeight: 700,
                  backgroundColor: 'rgba(255, 255, 255, 0.72)',
                  borderColor: 'rgba(122, 129, 151, 0.24)',
                  color: '#203864',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: 'white',
                    borderColor: 'rgba(122, 129, 151, 0.35)',
                  },
                }}
              >
                Consulter
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>

      {(isMobile || !showUploaderInline) && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              mt: 1.15,
              ml: { xs: 5, sm: 6 },
              pl: { xs: 0.25, sm: 0.5 },
            }}
          >
            <Box display="grid" gridTemplateColumns="1fr" gap={{ xs: 1, sm: 1.15, md: 1.35 }}>
              <Box minWidth={0}>
                <Typography
                  variant="caption"
                  sx={{ color: '#7A8197', fontSize: { xs: 10.5, sm: 11 }, fontWeight: 500, lineHeight: 1.2, mb: 0.35 }}
                >
                  Signé le
                </Typography>
                <Typography sx={{ color: '#203864', fontSize: 13, fontWeight: 400, lineHeight: 1.3 }}>
                  {new Date(uploadedAt).toLocaleDateString('fr-FR')}
                </Typography>
              </Box>

              <Box minWidth={0}>
                <Typography
                  variant="caption"
                  sx={{ color: '#7A8197', fontSize: { xs: 10.5, sm: 11 }, fontWeight: 500, lineHeight: 1.2, mb: 0.35 }}
                >
                  Par
                </Typography>
                <Typography sx={{ color: '#203864', fontSize: 13, fontWeight: 400, lineHeight: 1.3 }}>
                  {fullName}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Collapse>
      )}
    </Box>
  )
}
